/**
 * The bridge deckhouse (ADR 0033), lifted from brad-airship's
 * `airship-cockpit.js` `buildCockpit` as the exterior pass left it: authored
 * in the room's own space (local −z is forward, the windscreen; +x the room's
 * east side). Data places the room at rotation 90, so local north looks over
 * the bow and local east faces the street camera (world south).
 *
 * Construction logic, so every part has a job:
 * - A dark boarded wainscot (with a forward instrument console in the nose)
 *   carries a glazed "greenhouse": its posts lean inward about 12° (tumblehome)
 *   and the windshield rakes back like the references, so every window is a
 *   trapezoid. A heavy dark header beam ties the post tops.
 * - Doors are vertical casings let into the leaning wall, closed by leaves
 *   swung open over the solid bay next to them.
 * - The roof is cambered tarred planking with athwartship battens on rafters,
 *   a deep dark fascia and a pronounced eave. Stern ladder rungs reach it; it
 *   carries a skylight over the chart table, a searchlight on a railed
 *   platform, and a signal mast with the aether-scanner, masthead light and
 *   aerial whose lead-in cable drops through the port bulkhead to the console.
 * - Steering: wheel on a pedestal whose chain drops through a deck gland into a
 *   flush cable trunk aft; binnacle with lamp ahead; telegraph; voice pipe.
 *
 * Draw calls: all opaque parts share two vertex-coloured, planar-textured
 * materials (wood and paint, metal and fabric); plus glass, lamp glass and the
 * CRT. Parts the camera looks through (the camera-side walls and the roof) are
 * tagged for the roof reveal (`tagMesh(o, { reveal: room.id })`).
 *
 * What changed in the port: the materials are the kit's (`kit.m`, their hooks
 * through `material-hooks.ts`), the userData flags are the kit's typed flags,
 * and the random stream is `kit.random`. The arithmetic and every number are
 * the prototype's.
 */
import * as THREE from 'three';
import { addShaderHook } from '../material-hooks.ts';
import type { RoomBuild, Side } from './area.ts';
import { crtHook, planarHook } from './hooks.ts';
import { type ShipKit, type Vec3, at, flags, primitive, tagMesh } from './kit.ts';

type P2 = readonly [number, number];

const FACING: readonly Side[] = ['north', 'east', 'south', 'west'];
const PALETTE = { amber: 0, red: 1, green: 2, white: 3 } as const;
type LampKey = keyof typeof PALETTE;
const COLORS: Readonly<Record<string, readonly ['wood' | 'metal', string]>> = {
  // board-textured
  paint: ['wood', '#e3c893'],
  wood: ['wood', '#5b4030'],
  beam: ['wood', '#35261c'],
  roof: ['wood', '#8a7864'],
  tan: ['wood', '#c99f63'],
  batten: ['wood', '#4a3a2e'],
  frame: ['wood', '#d8b97f'],
  // mottled
  iron: ['metal', '#3b3835'],
  brass: ['metal', '#bb9352'],
  blue: ['metal', '#3f6d92'],
  red: ['metal', '#a8423a'],
  tile: ['metal', '#c9a26d'],
  grout: ['metal', '#4a3e33'],
  chart: ['metal', '#e9dfc4'],
  yellow: ['metal', '#d9b33c'],
  white: ['metal', '#ece6d6'],
  dial: ['metal', '#f1ead8'],
  leather: ['metal', '#2f2a27'],
};

/** The bridge's materials, made once per kit (`cockpitMaterials`). */
const cockpitMaterials = (kit: ShipKit): void => {
  const m = kit.m;
  if (m['bridgeWood'] !== undefined) return;
  const tex = kit.textures();
  const make = (name: string, texture: THREE.Texture, scale: number, opts: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial => {
    const mat = new THREE.MeshStandardMaterial({ color: '#ffffff', vertexColors: true, ...opts });
    mat.name = name;
    addShaderHook(mat, planarHook(texture, scale));
    return kit.finish(mat);
  };
  m['bridgeWood'] = make('airship-bridge-wood', tex.paint, 0.6, { roughness: 0.88 });
  const metal = make('airship-bridge-metal', tex.iron, 1.2, { roughness: 0.62, metalness: 0.25 });
  // Fittings: shadow detail.
  flags(metal).detailShadow = true;
  m['bridgeMetal'] = metal;
  // Glass: faintly green-blue, low opacity so the interior reads through it,
  // with a faint emissive sky tint instead of a reflection probe.
  const glass = new THREE.MeshStandardMaterial({ color: '#a9cbd2', emissive: '#7fa2b4', emissiveIntensity: 0.2, roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.22, depthWrite: false, side: THREE.DoubleSide });
  // Panes pass the sun: no shadow-pass draw.
  flags(glass).noShadow = true;
  glass.name = 'airship-bridge-glass';
  m['bridgeGlass'] = kit.finish(glass);
  // CRT vertex-emission material: black-green tube, luminous strokes.
  const crt = new THREE.MeshStandardMaterial({ color: '#ffffff', emissive: '#ffffff', emissiveIntensity: 0.68, vertexColors: true, roughness: 1 });
  crt.name = 'airship-crt-phosphor';
  flags(crt).noShadow = true;
  kit.finish(crt);
  addShaderHook(crt, crtHook);
  m['crtPhosphor'] = crt;
  // Lamp glass (lanterns, binnacle, navigation lights): colour from a 4-texel
  // palette addressed by UV, so the reveal clone keeps it. Night-glow contract.
  const data = new Uint8Array([255, 196, 110, 255, 235, 60, 50, 255, 70, 230, 110, 255, 255, 250, 235, 255]);
  const pal = kit.own(new THREE.DataTexture(data, 4, 1));
  pal.magFilter = THREE.NearestFilter;
  pal.minFilter = THREE.NearestFilter;
  pal.colorSpace = THREE.SRGBColorSpace;
  pal.needsUpdate = true;
  const lamp = new THREE.MeshStandardMaterial({ map: pal, emissiveMap: pal, emissive: '#ffffff', emissiveIntensity: 0.35, roughness: 0.3 });
  lamp.name = 'airship-bridge-lamp-glass';
  flags(lamp).nightGlow = 2.2;
  flags(lamp).noShadow = true;
  m['bridgeLamp'] = kit.finish(lamp);
};

const V = (x = 0, y = 0, z = 0): THREE.Vector3 => new THREE.Vector3(x, y, z);
const Vp = (p: Vec3): THREE.Vector3 => new THREE.Vector3(p[0], p[1], p[2]);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerp2 = (a: P2, b: P2, t: number): P2 => [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];

type SegType = 'glass' | 'door' | 'panel' | 'cheek';
type Seg = { a: number; b: number; readonly type: SegType };
type Edge = { readonly side: Side; readonly b0: P2; readonly b1: P2; readonly t0: P2; readonly t1: P2; readonly y0: number; readonly panes?: number; readonly rect?: boolean };

/** The bridge for `room` (its doors, workstations, size, rotation and level), in the room's own space. */
export const buildCockpit = (kit: ShipKit, room: RoomBuild): THREE.Group => {
  cockpitMaterials(kit);
  const seed = 1;
  const root = new THREE.Group();
  root.name = `cockpit:${room.id}:level-${room.level}`;
  const hw = room.width / 2;
  const hd = room.depth / 2;
  const cameraSide = at(FACING, (2 - room.rotation / 90 + 4) % 4);
  // While true, new meshes join the Octopath roof reveal.
  let rev = false;
  // Floor-level parts must not join the cutaway hole around Brad.
  let cut = true;
  // Fixtures may be built in their own rotated group.
  let parent: THREE.Object3D = root;
  const withRev = (on: boolean, f: () => void): void => {
    const was = rev;
    rev = on;
    f();
    rev = was;
  };
  // ---- Mesh helpers ------------------------------------------------------
  const tint = (g: THREE.BufferGeometry, hex: string): THREE.BufferGeometry => {
    const c = new THREE.Color(hex);
    const n = g.getAttribute('position').count;
    const v = new Float32Array(n * 3);
    for (let i = 0; i < n; i += 1) v.set([c.r, c.g, c.b], i * 3);
    g.setAttribute('color', new THREE.Float32BufferAttribute(v, 3));
    return g;
  };
  const P = (k: string, g: THREE.BufferGeometry, x = 0, y = 0, z = 0): THREE.Mesh => {
    let mat: THREE.Material;
    const c = COLORS[k];
    if (c !== undefined) {
      const [family, hex] = c;
      mat = kit.get(family === 'wood' ? 'bridgeWood' : 'bridgeMetal');
      tint(g, hex);
    } else mat = kit.get(k);
    const o = primitive(parent, mat, g, x, y, z, cut);
    if (rev) tagMesh(o, { reveal: room.id });
    return o;
  };
  const B = (k: string, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => P(k, new THREE.BoxGeometry(w, h, d), x, y, z);
  const C = (k: string, x: number, y: number, z: number, r: number, h: number, n = 8, r2 = r): THREE.Mesh => P(k, new THREE.CylinderGeometry(r, r2, h, n), x, y, z);
  const Cz = (k: string, x: number, y: number, z: number, r: number, h: number, n = 10, r2 = r): THREE.Mesh => P(k, new THREE.CylinderGeometry(r, r2, h, n).rotateX(Math.PI / 2), x, y, z);
  const Cx = (k: string, x: number, y: number, z: number, r: number, h: number, n = 10): THREE.Mesh => P(k, new THREE.CylinderGeometry(r, r, h, n).rotateZ(Math.PI / 2), x, y, z);
  // Closed hexahedron from two rings of four points (flat shaded).
  const hexa = (k: string, p: readonly THREE.Vector3[]): THREE.Mesh => {
    const c = p.reduce((s, q) => s.add(q), V()).multiplyScalar(1 / 8);
    const pos: number[] = [];
    for (const f of [
      [0, 1, 2, 3],
      [4, 5, 6, 7],
      [0, 1, 5, 4],
      [1, 2, 6, 5],
      [2, 3, 7, 6],
      [3, 0, 4, 7],
    ] as const) {
      const a = at(p, f[0]);
      const b = at(p, f[1]);
      const cc = at(p, f[2]);
      const d = at(p, f[3]);
      const fc = V().add(a).add(b).add(cc).add(d).multiplyScalar(0.25);
      const n = V().subVectors(b, a).cross(V().subVectors(cc, a));
      if (n.lengthSq() < 1e-12) n.subVectors(cc, a).cross(V().subVectors(d, a));
      const tri = n.dot(fc.clone().sub(c)) >= 0 ? [a, b, cc, a, cc, d] : [a, cc, b, a, d, cc];
      for (const v of tri) pos.push(v.x, v.y, v.z);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
    g.computeVertexNormals();
    return P(k, g);
  };
  // Box from a to b with cross-section w (along side axis) by d.
  const strut = (k: string, a0: Vec3, b0: Vec3, w: number, d: number, hint = V(0, 1, 0)): THREE.Mesh => {
    const a = Vp(a0);
    const b = Vp(b0);
    const dir = V().subVectors(b, a).normalize();
    let s = V().crossVectors(dir, hint);
    if (s.lengthSq() < 1e-6) s = V().crossVectors(dir, V(1, 0, 0));
    s.normalize();
    const t = V().crossVectors(s, dir).normalize();
    const sw = s.multiplyScalar(w / 2);
    const td = t.multiplyScalar(d / 2);
    const q: THREE.Vector3[] = [];
    for (const e of [a, b])
      for (const [i, j] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const)
        q.push(e.clone().addScaledVector(sw, i).addScaledVector(td, j));
    return hexa(k, q);
  };
  // Slab from a planar quad, thickened along its normal.
  const slab = (k: string, quadPts: readonly Vec3[], th: number): THREE.Mesh => {
    const q = quadPts.map(Vp);
    const q0 = at(q, 0);
    const n = V().subVectors(at(q, 1), q0).cross(V().subVectors(at(q, 2), q0));
    if (n.lengthSq() < 1e-10) n.subVectors(at(q, 2), q0).cross(V().subVectors(at(q, 3), q0));
    n.normalize().multiplyScalar(th / 2);
    return hexa(k, [...q.map((p) => p.clone().add(n)), ...q.map((p) => p.clone().sub(n))]);
  };
  // Single-sided quad (glass) facing `out`.
  const quad = (k: string, pts: readonly Vec3[], out: Vec3): THREE.Mesh => {
    const [a, b, c, d] = [Vp(at(pts, 0)), Vp(at(pts, 1)), Vp(at(pts, 2)), Vp(at(pts, 3))];
    const n = V().subVectors(b, a).cross(V().subVectors(c, a));
    const order = n.dot(Vp(out)) >= 0 ? [a, b, c, a, c, d] : [a, c, b, a, d, c];
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(order.flatMap((v) => v.toArray()), 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(12), 2));
    g.computeVertexNormals();
    return P(k, g);
  };
  const lampUV = (g: THREE.BufferGeometry, key: LampKey): THREE.BufferGeometry => {
    const u = (PALETTE[key] + 0.5) / 4;
    const uv = g.getAttribute('uv');
    for (let i = 0; i < uv.count; i += 1) uv.setXY(i, u, 0.5);
    return g;
  };
  const lampBox = (key: LampKey, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => P('bridgeLamp', lampUV(new THREE.BoxGeometry(w, h, d), key), x, y, z);
  const lampBall = (key: LampKey, x: number, y: number, z: number, r: number): THREE.Mesh => P('bridgeLamp', lampUV(new THREE.SphereGeometry(r, 8, 5), key), x, y, z);
  const crtTint = (o: THREE.Mesh, hex: string): THREE.Mesh => {
    tint(o.geometry, hex);
    return o;
  };
  const crt = (hex: string, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => crtTint(P('crtPhosphor', new THREE.BoxGeometry(w, h, d), x, y, z), hex);
  const crtG = (hex: string, g: THREE.BufferGeometry, x: number, y: number, z: number): THREE.Mesh => crtTint(P('crtPhosphor', g, x, y, z), hex);

  // ---- Dimensions --------------------------------------------------------
  const SILL = 0.82;
  const SILL_TOP = 0.9;
  const TOPY = 2.3;
  const BEAM_H = 0.32;
  const LINTEL = 2.12;
  const LEAN = 0.3;
  const doorHalf = (room.localDoors[0]?.width ?? 1.4) / 2;
  const item = (k: string): RoomBuild['localInteractions'][number] | undefined => room.localInteractions.find((i) => i.kind === k);
  const consoleItem = item('console');
  // Plan outlines: B at the sill (wall line + nose), T at the post tops.
  const NOSE = { z: -hd - 0.9, x: hw - 1 };
  const TNOSE = { z: -hd - 0.15, x: hw - 1.4 };
  const EAVE = hw + 0.5;
  const EAVE_Y = TOPY + BEAM_H + 0.08;
  const CROWN = 0.45;
  const roofY = (x: number): number => EAVE_Y + CROWN * (1 - Math.min(1, (x / EAVE) ** 2));
  const topSide = (sx: number, z: number): P2 => [sx * (hw - LEAN), lerp(-hd + 1.0, hd - 0.45, (z + hd) / (2 * hd))];

  // ---- Floor: tan tiles on a dark bed, distinct from the deck planks ------
  cut = false;
  for (const s of room.localSurfaces) {
    B('grout', (s.x0 + s.x1) / 2, -0.13, (s.z0 + s.z1) / 2, s.x1 - s.x0, 0.26, s.z1 - s.z0);
    const t = 0.5;
    for (let x = s.x0; x < s.x1 - 1e-6; x += t) for (let z = s.z0; z < s.z1 - 1e-6; z += t) B('tile', x + t / 2, 0.006, z + t / 2, t - 0.025, 0.012, t - 0.025);
  }
  cut = true;

  // ---- Walls: edges of the greenhouse loop --------------------------------
  // Each edge: bottom plan points b0->b1 (sill line), top plan points t0->t1,
  // the side it belongs to, and the base height of its wainscot.
  const doorsOn = (side: Side): number[] => room.localDoors.filter((d) => d.side === side).map((d) => (side === 'north' || side === 'south' ? d.x : d.z));
  const rectEdge = (side: Side, b0: P2, b1: P2, t0: P2, t1: P2): Edge => ({ side, b0, b1, t0, t1, y0: 0, rect: true });
  const edges: readonly Edge[] = [
    rectEdge('east', [hw, hd], [hw, -hd], topSide(1, hd), topSide(1, -hd)),
    { side: 'north', b0: [hw, -hd], b1: [NOSE.x, NOSE.z], t0: topSide(1, -hd), t1: [TNOSE.x, TNOSE.z], y0: 0.03 },
    { side: 'north', b0: [NOSE.x, NOSE.z], b1: [-NOSE.x, NOSE.z], t0: [TNOSE.x, TNOSE.z], t1: [-TNOSE.x, TNOSE.z], y0: 0.03, panes: 3 },
    { side: 'north', b0: [-NOSE.x, NOSE.z], b1: [-hw, -hd], t0: [-TNOSE.x, TNOSE.z], t1: topSide(-1, -hd), y0: 0.03 },
    rectEdge('west', [-hw, -hd], [-hw, hd], topSide(-1, -hd), topSide(-1, hd)),
    rectEdge('south', [-hw, hd], [hw, hd], topSide(-1, hd), topSide(1, hd)),
  ];
  const camDirs: Readonly<Record<Side, P2>> = { north: [0, -1], east: [1, 0], south: [0, 1], west: [-1, 0] };
  const prio: Readonly<Record<SegType, number>> = { door: 3, panel: 2, cheek: 1, glass: 0 };
  for (const e of edges) {
    const L = Math.hypot(e.b1[0] - e.b0[0], e.b1[1] - e.b0[1]);
    const dir: P2 = [(e.b1[0] - e.b0[0]) / L, (e.b1[1] - e.b0[1]) / L];
    let out: P2 = [dir[1], -dir[0]];
    const mid = lerp2(e.b0, e.b1, 0.5);
    if (out[0] * mid[0] + out[1] * mid[1] < 0) out = [-out[0], -out[1]];
    const horizontal = e.side === 'north' || e.side === 'south';
    const alongEdge = (p: number): number => (p - (horizontal ? e.b0[0] : e.b0[1])) / (horizontal ? e.b1[0] - e.b0[0] : e.b1[1] - e.b0[1]);
    // Segment types over fractions: glass by default; doors, bays and panels carve in.
    const marks: (readonly [number, number, SegType])[] = [];
    if (e.rect === true) {
      for (const c of doorsOn(e.side)) {
        const f0 = alongEdge(c - doorHalf);
        const f1 = alongEdge(c + doorHalf);
        const lo = Math.min(f0, f1);
        const hi = Math.max(f0, f1);
        marks.push([lo, hi, 'door']);
        // The leaf parks over the solid bay toward the nearer corner.
        const toStart = lo;
        const toEnd = 1 - hi;
        if (toStart <= toEnd) {
          marks.push([0, lo, 'panel']);
          marks.push([hi, hi + 0.35 / L, 'cheek']);
        } else {
          marks.push([hi, 1, 'panel']);
          marks.push([lo - 0.35 / L, lo, 'cheek']);
        }
      }
      const lockerItem = item('flag-locker');
      if (e.side === 'west' && lockerItem !== undefined) {
        const f0 = alongEdge(lockerItem.z - 0.75);
        const f1 = alongEdge(lockerItem.z + 0.75);
        marks.push([Math.min(f0, f1), Math.max(f0, f1), 'panel']);
      }
      if (e.side === 'south') {
        const f0 = alongEdge(hw - 0.75);
        marks.push([Math.min(f0, 1), 1, 'panel']);
      }
    }
    const cuts = [...new Set([0, 1, ...marks.flatMap((q) => [q[0], q[1]])].map((v) => Number(Math.min(1, Math.max(0, v)).toFixed(5))))].sort((a, b) => a - b);
    let segs: Seg[] = [];
    for (let i = 0; i < cuts.length - 1; i += 1) {
      const a = at(cuts, i);
      const b = at(cuts, i + 1);
      const mf = (a + b) / 2;
      let type: SegType = 'glass';
      for (const [lo, hi, t] of marks) if (mf > lo && mf < hi && prio[t] > prio[type]) type = t;
      const prev = segs[segs.length - 1];
      if (prev !== undefined && prev.type === type && type !== 'door') prev.b = b;
      else segs.push({ a, b, type });
    }
    // Glass runs split into panes of at most ~1.35 m.
    segs = segs.flatMap((s): Seg[] => {
      if (s.type !== 'glass') return [s];
      const n = e.panes ?? Math.max(1, Math.round(((s.b - s.a) * L) / 1.3));
      const res: Seg[] = [];
      for (let j = 0; j < n; j += 1) res.push({ a: lerp(s.a, s.b, j / n), b: lerp(s.a, s.b, (j + 1) / n), type: 'glass' });
      return res;
    });
    const bot = (f: number, y: number): Vec3 => {
      const p = lerp2(e.b0, e.b1, f);
      return [p[0], y, p[1]];
    };
    const top = (f: number, y = TOPY): Vec3 => {
      const p = lerp2(e.t0, e.t1, f);
      return [p[0], y, p[1]];
    };
    const off = (p: Vec3, o: number): Vec3 => [p[0] + out[0] * o, p[1], p[2] + out[1] * o];
    const isJamb = (f: number): boolean => segs.some((s) => s.type === 'door' && (Math.abs(s.a - f) < 1e-6 || Math.abs(s.b - f) < 1e-6));
    // Camera-facing: the camera-side wall, plus any nose facet angled toward it.
    const camDir = camDirs[cameraSide];
    const cam = e.side === cameraSide || out[0] * camDir[0] + out[1] * camDir[1] > 0.3;
    // Wainscot under every non-door segment: kick strip, boards, cream cap.
    for (const s of segs) {
      if (s.type === 'door') continue;
      const ext = 0.09 / L;
      const A = bot(Math.max(0, s.a - (s.a === 0 ? ext : 0)), 0);
      const Bp = bot(Math.min(1, s.b + (s.b === 1 ? ext : 0)), 0);
      const seg = (k: string, y0: number, y1: number, th: number): THREE.Mesh => strut(k, [A[0], (y0 + y1) / 2, A[2]], [Bp[0], (y0 + y1) / 2, Bp[2]], th, y1 - y0);
      seg('iron', e.y0, 0.12, 0.22);
      seg('wood', Math.max(0.12, e.y0), SILL, 0.18);
      seg('paint', SILL, SILL_TOP, 0.3);
    }
    // Greenhouse: posts at every station, glass or panels between them.
    withRev(cam, () => {
      for (const s of segs) {
        if (s.type === 'glass') quad('bridgeGlass', [bot(s.a, SILL_TOP), bot(s.b, SILL_TOP), top(s.b), top(s.a)], [out[0], 0.2, out[1]]);
        else if (s.type === 'panel') slab('wood', [bot(s.a, SILL_TOP), bot(s.b, SILL_TOP), top(s.b), top(s.a)], 0.1);
        else if (s.type === 'cheek') slab('wood', [bot(s.a, SILL_TOP), bot(s.b, SILL_TOP), top(s.b), top(s.a)], 0.1);
      }
      const stations = [...new Set(segs.flatMap((s) => [s.a, s.b]))].filter((f) => f < 1 - 1e-6);
      for (const f of stations) {
        if (isJamb(f)) continue;
        const corner = f < 1e-6;
        strut('frame', bot(f, SILL_TOP - 0.02), top(f, TOPY + 0.02), corner ? 0.24 : 0.17, corner ? 0.22 : 0.18, V(out[0], 0, out[1]));
      }
      // Heavy dark header beam along the post tops.
      const t0 = top(0, TOPY + BEAM_H / 2);
      const t1 = top(1, TOPY + BEAM_H / 2);
      const d = V(t1[0] - t0[0], 0, t1[2] - t0[2]).normalize().multiplyScalar(0.12);
      strut('frame', [t0[0] - d.x, t0[1], t0[2] - d.z], [t1[0] + d.x, t1[1], t1[2] + d.z], 0.26, BEAM_H);
      // Close the camber gap between the header and the roof underside.
      const n = 6;
      for (let j = 0; j < n; j += 1) {
        const a = top(j / n, TOPY + BEAM_H - 0.02);
        const b = top((j + 1) / n, TOPY + BEAM_H - 0.02);
        const ay = roofY(a[0]) - 0.06;
        const by = roofY(b[0]) - 0.06;
        if (ay - a[1] > 0.03 || by - b[1] > 0.03)
          slab(
            'beam',
            [
              a,
              b,
              [b[0], by, b[2]],
              [a[0], ay, a[2]],
            ],
            0.16,
          );
      }
    });
    // Posts continue down through the boarding as cream stiles (not at doors).
    for (const s of segs) {
      const f = s.a;
      if (!isJamb(f) && f > 1e-6 && s.type !== 'door') strut('frame', bot(f, 0.12), bot(f, SILL), 0.14, 0.22, V(out[0], 0, out[1]));
    }
    // Door casings: vertical jambs, lintel, sloped hood back to the header,
    // side returns, threshold, and a leaf swung open over the bay.
    for (const s of segs.filter((q) => q.type === 'door')) {
      const jA = bot(s.a, 0);
      const jB = bot(s.b, 0);
      for (const [j, f] of [
        [jA, s.a],
        [jB, s.b],
      ] as const) {
        strut('paint', [j[0], 0, j[2]], [j[0], SILL_TOP, j[2]], 0.12, 0.26, V(out[0], 0, out[1]));
        withRev(cam, () => {
          strut('paint', [j[0], SILL_TOP, j[2]], [j[0], LINTEL + 0.1, j[2]], 0.12, 0.26, V(out[0], 0, out[1]));
          slab('wood', [[j[0], SILL_TOP, j[2]], [j[0], LINTEL + 0.1, j[2]], top(f), [j[0], SILL_TOP, j[2]]], 0.08);
        });
      }
      withRev(cam, () => {
        strut('paint', [jA[0], LINTEL + 0.05, jA[2]], [jB[0], LINTEL + 0.05, jB[2]], 0.28, 0.12);
        slab('wood', [[jA[0], LINTEL + 0.11, jA[2]], [jB[0], LINTEL + 0.11, jB[2]], top(s.b, TOPY), top(s.a, TOPY)], 0.07);
      });
      strut('iron', [jA[0], 0.01, jA[2]], [jB[0], 0.01, jB[2]], 0.34, 0.02);
      // Leaf: hinged on the jamb nearer the solid bay and swung right open,
      // standing a little off the wall on a hook (inside the collider band).
      const hingeF = s.a <= 1 - s.b ? s.a : s.b;
      const towardF = s.a <= 1 - s.b ? -1 : 1;
      const lw = 2 * doorHalf - 0.12;
      const ang = 0.09;
      const hinge = off(bot(hingeF, 0), 0.13);
      const tan = Vp(bot(hingeF + towardF * 0.5, 0)).sub(Vp(bot(hingeF, 0))).normalize();
      const tip: Vec3 = [hinge[0] + (tan.x * Math.cos(ang) + out[0] * Math.sin(ang)) * lw, 0, hinge[2] + (tan.z * Math.cos(ang) + out[1] * Math.sin(ang)) * lw];
      const along = (t: number, o = 0): P2 => [lerp(hinge[0], tip[0], t) + out[0] * o, lerp(hinge[2], tip[2], t) + out[1] * o];
      const leaf = (k: string, y0: number, y1: number, th: number, t0 = 0, t1 = 1, o = 0): THREE.Mesh => {
        const a = along(t0, o);
        const b = along(t1, o);
        return strut(k, [a[0], (y0 + y1) / 2, a[1]], [b[0], (y0 + y1) / 2, b[1]], th, y1 - y0);
      };
      // We see its inner face: ledged-and-braced boards, the unmistakable back
      // of an opened door.
      const brace = (y0: number, y1: number): void => {
        const a = along(0.1, 0.045);
        const b = along(0.9, 0.045);
        strut('frame', [a[0], y0, a[1]], [b[0], y1, b[1]], 0.1, 0.03, V(out[0], 0, out[1]));
      };
      leaf('wood', 0.03, SILL_TOP, 0.06);
      for (const y of [0.2]) leaf('frame', y, y + 0.12, 0.09, 0.03, 0.97);
      brace(0.3, SILL_TOP - 0.05);
      withRev(cam, () => {
        leaf('wood', SILL_TOP, LINTEL - 0.03, 0.06);
        for (const y of [1.2, 1.95]) leaf('frame', y, y + 0.12, 0.09, 0.03, 0.97);
        leaf('frame', SILL_TOP - 0.06, SILL_TOP + 0.06, 0.09, 0.03, 0.97);
        // Round porthole light in the upper leaf.
        const pc = along(0.5, 0.06);
        const rot = Math.atan2(tip[0] - hinge[0], tip[2] - hinge[2]);
        const ring = P('iron', new THREE.TorusGeometry(0.17, 0.035, 4, 12), pc[0], 1.7, pc[1]);
        ring.rotation.y = rot + Math.PI / 2;
        const gl = P('bridgeGlass', new THREE.CircleGeometry(0.16, 12), pc[0], 1.7, pc[1]);
        gl.rotation.y = rot + Math.PI / 2;
        // Lantern over the doorway on a bracket from the hood.
        const c = off(bot((s.a + s.b) / 2, 0), 0.34);
        strut('iron', [c[0] - out[0] * 0.3, LINTEL + 0.3, c[2] - out[1] * 0.3], [c[0], LINTEL + 0.3, c[2]], 0.03, 0.03);
        P('iron', new THREE.CylinderGeometry(0.05, 0.1, 0.07, 6), c[0], LINTEL + 0.24, c[2]);
        P('bridgeLamp', lampUV(new THREE.CylinderGeometry(0.06, 0.06, 0.16, 6), 'amber'), c[0], LINTEL + 0.12, c[2]);
        P('iron', new THREE.CylinderGeometry(0.08, 0.08, 0.03, 6), c[0], LINTEL + 0.03, c[2]);
        const h = along(0, 0.02);
        const e2 = along(0.35, 0.05);
        strut('iron', [h[0] - out[0] * 0.12, 1.9, h[1] - out[1] * 0.12], [e2[0], 1.9, e2[1]], 0.06, 0.1);
      });
      // Strap hinges from the jamb across the leaf, and the hold-back hook.
      for (const y of [0.35, 0.95]) {
        const h = along(0, 0.02);
        const e2 = along(0.35, 0.05);
        strut('iron', [h[0] - out[0] * 0.12, y, h[1] - out[1] * 0.12], [e2[0], y, e2[1]], 0.06, 0.1);
      }
      const hk = along(0.97, 0.03);
      const wall = off(bot(hingeF + (towardF * (lw - 0.05)) / L, 0), 0.08);
      strut('brass', [hk[0], 1.0, hk[1]], [wall[0], 1.0, wall[2]], 0.025, 0.025);
      const hp = along(0.9, 0.07);
      strut('brass', [hp[0], 0.95, hp[1]], [hp[0], 1.2, hp[1]], 0.03, 0.03);
    }
  }

  // ---- Forward instrument console filling the nose -------------------------
  {
    const ring = (y: number, i: number): THREE.Vector3[] => [V(hw - 0.1, y, -hd + 0.1), V(NOSE.x - 0.05 - i, y, NOSE.z + 0.08), V(-NOSE.x + 0.05 + i, y, NOSE.z + 0.08), V(-hw + 0.1, y, -hd + 0.1)];
    hexa('leather', [...ring(SILL - 0.04, 0), ...ring(SILL + 0.03, 0)]);
    strut('wood', [-hw + 0.1, SILL / 2, -hd + 0.1], [hw - 0.1, SILL / 2, -hd + 0.1], 0.06, SILL);
    strut('iron', [-hw + 0.1, 0.06, -hd + 0.12], [hw - 0.1, 0.06, -hd + 0.12], 0.08, 0.12);
    // Gauges (engine revs, lift pressure, air speed) and a clinometer, facing aft.
    for (const [x, r] of [
      [-1.3, 0.11],
      [-0.8, 0.11],
      [0.8, 0.11],
      [1.3, 0.11],
    ] as const) {
      Cz('brass', x, SILL + 0.1, -hd - 0.25, r + 0.02, 0.05, 12);
      Cz('dial', x, SILL + 0.1, -hd - 0.22, r, 0.02, 12);
      strut('iron', [x, SILL + 0.1, -hd - 0.2], [x + r * 0.6, SILL + 0.16, -hd - 0.2], 0.012, 0.01);
    }
    Cz('brass', 0, SILL + 0.15, -hd - 0.35, 0.16, 0.04, 14);
    Cz('dial', 0, SILL + 0.15, -hd - 0.325, 0.14, 0.02, 14);
  }

  // ---- Roof ----------------------------------------------------------------
  const zFront = NOSE.z - 0.1;
  const zBack = hd + 0.12;
  const zShoulder = -hd + 0.15;
  const halfW = (z: number): number => (z >= zShoulder ? EAVE : lerp(NOSE.x + 0.1, EAVE, (z - zFront) / (zShoulder - zFront)));
  withRev(true, () => {
    const zs = [zFront];
    for (let z = Math.ceil(zFront * 2) / 2; z < zBack; z += 0.5) if (z > zFront + 0.05) zs.push(z);
    zs.push(zShoulder);
    zs.push(zBack);
    zs.sort((a, b) => a - b);
    const U = 10;
    const pos: number[] = [];
    const under: number[] = [];
    const pt = (z: number, u: number, dy = 0): Vec3 => {
      const x = u * halfW(z);
      return [x, roofY(x) + dy, z];
    };
    for (let i = 0; i < zs.length - 1; i += 1) {
      for (let j = 0; j < U; j += 1) {
        const u0 = -1 + (2 * j) / U;
        const u1 = -1 + (2 * (j + 1)) / U;
        const z0 = at(zs, i);
        const z1 = at(zs, i + 1);
        const a = pt(z0, u0);
        const b = pt(z0, u1);
        const c = pt(z1, u1);
        const d = pt(z1, u0);
        pos.push(...a, ...c, ...b, ...a, ...d, ...c);
        const A = pt(z0, u0, -0.08);
        const Bq = pt(z0, u1, -0.08);
        const Cq = pt(z1, u1, -0.08);
        const D = pt(z1, u0, -0.08);
        under.push(...A, ...Bq, ...Cq, ...A, ...Cq, ...D);
      }
    }
    // Fascia: a deep dark board all round the eave, hanging below the planking.
    const fascia: number[] = [];
    const FD = 0.26;
    const ring: Vec3[] = [];
    for (const z of zs) ring.push(pt(z, 1));
    for (let j = U; j >= 0; j -= 1) ring.push(pt(zBack, -1 + (2 * j) / U));
    for (const z of [...zs].reverse()) ring.push(pt(z, -1));
    for (let j = 0; j <= U; j += 1) ring.push(pt(zFront, -1 + (2 * j) / U));
    for (let i = 0; i < ring.length - 1; i += 1) {
      const a = at(ring, i);
      const b = at(ring, i + 1);
      if (Math.hypot(a[0] - b[0], a[2] - b[2]) < 1e-6) continue;
      const a2: Vec3 = [a[0], a[1] - FD, a[2]];
      const b2: Vec3 = [b[0], b[1] - FD, b[2]];
      const a1: Vec3 = [a[0], a[1] + 0.03, a[2]];
      const b1: Vec3 = [b[0], b[1] + 0.03, b[2]];
      fascia.push(...a1, ...b1, ...b2, ...a1, ...b2, ...a2);
    }
    const mk = (arr: readonly number[], k: string, double: boolean): THREE.Mesh => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((arr.length / 3) * 2), 2));
      g.computeVertexNormals();
      const o = P(k, g);
      if (double) {
        const f: number[] = [];
        for (let i = 0; i < arr.length; i += 9) f.push(...arr.slice(i, i + 3), ...arr.slice(i + 6, i + 9), ...arr.slice(i + 3, i + 6));
        mk(f, k, false);
      }
      return o;
    };
    const vAt = (arr: readonly number[], i: number): THREE.Vector3 => V(at(arr, i), at(arr, i + 1), at(arr, i + 2));
    // Make the top face outward (up) and the underside face down.
    const up: number[] = [];
    for (let i = 0; i < pos.length; i += 9) {
      const a = vAt(pos, i);
      const b = vAt(pos, i + 3);
      const c = vAt(pos, i + 6);
      const n = V().subVectors(b, a).cross(V().subVectors(c, a));
      up.push(...(n.y >= 0 ? [...a.toArray(), ...b.toArray(), ...c.toArray()] : [...a.toArray(), ...c.toArray(), ...b.toArray()]));
    }
    const dn: number[] = [];
    for (let i = 0; i < under.length; i += 9) {
      const a = vAt(under, i);
      const b = vAt(under, i + 3);
      const c = vAt(under, i + 6);
      const n = V().subVectors(b, a).cross(V().subVectors(c, a));
      dn.push(...(n.y <= 0 ? [...a.toArray(), ...b.toArray(), ...c.toArray()] : [...a.toArray(), ...c.toArray(), ...b.toArray()]));
    }
    const roof = mk(up, 'roof', false);
    mk(dn, 'beam', false);
    mk(fascia, 'beam', true);
    // Tarred canvas in cloths between battens: each strip weathered a little
    // differently, with a few darker tar patches.
    {
      const col = roof.geometry.getAttribute('color');
      const rnd = kit.random(seed * 977 + 13);
      const rows = zs.length - 1;
      const rowK = Array.from({ length: rows }, () => 0.9 + rnd() * 0.2);
      for (let q = 0; q < col.count / 6; q += 1) {
        const k = at(rowK, Math.floor(q / U)) * (rnd() < 0.08 ? 0.8 : 0.96 + rnd() * 0.08);
        for (let v = 0; v < 6; v += 1) {
          const i = q * 6 + v;
          col.setXYZ(i, col.getX(i) * k, col.getY(i) * k, col.getZ(i) * k);
        }
      }
    }
    // Painted drip-edge capping along the top of the fascia.
    for (let i = 0; i < ring.length - 1; i += 1) {
      const a = at(ring, i);
      const b = at(ring, i + 1);
      if (Math.hypot(a[0] - b[0], a[2] - b[2]) < 1e-6) continue;
      strut('frame', [a[0], a[1] + 0.03, a[2]], [b[0], b[1] + 0.03, b[2]], 0.09, 0.07);
    }
    // Battens athwartships over the canvas, following the camber.
    for (let z = zFront + 0.45; z < zBack - 0.2; z += 0.85) {
      const n = 6;
      for (let j = 0; j < n; j += 1) {
        const a = pt(z, -0.97 + (1.94 * j) / n, 0.025);
        const b = pt(z, -0.97 + (1.94 * (j + 1)) / n, 0.025);
        strut('batten', a, b, 0.07, 0.05);
      }
    }
    // Rafters/roof beams bearing on the headers; their tails show under the eave.
    for (let z = -hd + 0.9; z < hd; z += 1.6) for (const s of [-1, 1]) strut('beam', [s * (EAVE - 0.12), roofY(EAVE - 0.12) - 0.16, z], [0, roofY(0) - 0.16, z], 0.14, 0.16);
    // Skylight over the chart table: coaming, pitched glazed lids, guard bars.
    const chart = item('chart-table');
    if (chart !== undefined) {
      const sx = chart.x;
      const sz = chart.z - 0.35;
      const y = roofY(sx);
      B('beam', sx, y + 0.1, sz, 1.0, 0.22, 0.7);
      for (const s of [-1, 1]) {
        quad(
          'bridgeGlass',
          [
            [sx - 0.48, y + 0.21, sz + s * 0.34],
            [sx + 0.48, y + 0.21, sz + s * 0.34],
            [sx + 0.48, y + 0.36, sz],
            [sx - 0.48, y + 0.36, sz],
          ],
          [0, 1, s],
        );
        for (const dx of [-0.3, 0, 0.3]) strut('brass', [sx + dx, y + 0.23, sz + s * 0.33], [sx + dx, y + 0.38, sz], 0.025, 0.025);
      }
      strut('beam', [sx - 0.5, y + 0.37, sz], [sx + 0.5, y + 0.37, sz], 0.06, 0.05);
    }
    // Searchlight on a small railed platform at the front of the roof.
    const pz = zShoulder - 0.1;
    const py = roofY(0) + 0.06;
    B('beam', 0, py, pz, 1.1, 0.05, 0.9);
    for (let x = -0.45; x <= 0.46; x += 0.15) B('wood', x, py + 0.04, pz, 0.1, 0.03, 0.86);
    for (const [x, z] of [
      [-0.52, -0.42],
      [0.52, -0.42],
      [-0.52, 0.42],
      [0.52, 0.42],
    ] as const)
      strut('iron', [x, py, z + pz], [x, py + 0.55, z + pz], 0.03, 0.03);
    for (const s of [-1, 1]) {
      strut('iron', [s * 0.52, py + 0.55, pz - 0.42], [s * 0.52, py + 0.55, pz + 0.42], 0.03, 0.03);
      strut('iron', [-0.52, py + 0.55, pz + s * 0.42], [0.52, py + 0.55, pz + s * 0.42], 0.03, 0.03);
    }
    C('iron', 0, py + 0.16, pz, 0.1, 0.22, 6, 0.14);
    B('iron', 0, py + 0.3, pz, 0.5, 0.05, 0.12);
    for (const s of [-1, 1]) B('iron', s * 0.22, py + 0.44, pz, 0.04, 0.28, 0.08);
    Cz('paint', 0, py + 0.5, pz, 0.19, 0.42, 10);
    P('bridgeGlass', new THREE.CylinderGeometry(0.16, 0.16, 0.02, 10).rotateX(Math.PI / 2), 0, py + 0.5, pz - 0.22);
    C('iron', 0, py + 0.72, pz + 0.05, 0.05, 0.06, 6);
    // Signal mast in a tabernacle, stayed to the eaves.
    const mz = hd - 1.6;
    const mY = roofY(0);
    const topM = 5.4;
    B('iron', 0, mY + 0.18, mz, 0.3, 0.36, 0.3);
    strut('wood', [0, mY, mz], [0, topM, mz], 0.12, 0.12);
    for (const s of [-1, 1]) strut('iron', [0, topM - 0.7, mz], [s * (EAVE - 0.3), roofY(EAVE - 0.3), mz + 0.6], 0.02, 0.02);
    strut('iron', [0, topM - 0.7, mz], [0, roofY(0) + 0.02, mz - 2.4], 0.02, 0.02);
    // Aether-scanner: a slotted bar antenna on a small platform.
    B('iron', 0, topM - 0.9, mz, 0.5, 0.05, 0.5);
    C('iron', 0, topM - 0.78, mz, 0.06, 0.2, 6);
    B('paint', 0, topM - 0.66, mz, 1.3, 0.1, 0.14);
    B('iron', 0, topM - 0.66, mz + 0.08, 1.2, 0.05, 0.02);
    // Masthead light (white) and a radio whip at the truck.
    B('iron', 0, topM + 0.02, mz - 0.12, 0.06, 0.06, 0.2);
    lampBox('white', 0, topM + 0.08, mz - 0.22, 0.12, 0.16, 0.12);
    strut('iron', [0, topM, mz], [0, topM + 0.45, mz], 0.02, 0.02);
    // Lead-in cable: down the mast, across the roof, over the port eave.
    const cz = consoleItem?.z ?? -3;
    const cab: readonly Vec3[] = [
      [0.08, topM - 0.8, mz],
      [0.08, mY + 0.05, mz],
      [-hw * 0.5, roofY(hw * 0.5) + 0.02, cz],
      [-EAVE + 0.05, roofY(EAVE) + 0.04, cz],
      [-EAVE - 0.08, EAVE_Y - 0.05, cz],
      [-EAVE - 0.08, EAVE_Y - 0.35, cz],
    ];
    for (let j = 0; j < cab.length - 1; j += 1) strut('iron', at(cab, j), at(cab, j + 1), 0.035, 0.035);
    // Gutters along the side eaves, discharging into stern downpipes.
    for (const s of [-1, 1]) strut('iron', [s * (EAVE + 0.06), EAVE_Y - 0.2, zShoulder], [s * (EAVE + 0.06), EAVE_Y - 0.2, zBack], 0.1, 0.08);
  });
  // Lead-in continues down to a gland in the port bulkhead above the console.
  {
    const cz = consoleItem?.z ?? -3;
    const top: Vec3 = [-EAVE - 0.08, EAVE_Y - 0.35, cz];
    const mid: Vec3 = [-hw - 0.16, SILL_TOP + 0.2, cz];
    const gl: Vec3 = [-hw - 0.12, 0.6, cz];
    strut('iron', top, mid, 0.035, 0.035);
    strut('iron', mid, gl, 0.035, 0.035);
    B('brass', gl[0] + 0.02, gl[1], gl[2], 0.06, 0.1, 0.1);
  }
  // Downpipes from the gutters to deck scuppers at the stern corners.
  for (const s of [-1, 1]) {
    const x = s * (hw + 0.2);
    const z = hd + 0.2;
    const camCorner = (s > 0 ? 'east' : 'west') === cameraSide;
    withRev(camCorner, () => strut('iron', [s * (EAVE + 0.06), EAVE_Y - 0.22, z], [x, EAVE_Y - 0.5, z], 0.07, 0.07));
    strut('iron', [x, EAVE_Y - 0.5, z], [x, 0.12, z], 0.07, 0.07);
    B('iron', x, 0.06, z, 0.16, 0.12, 0.16);
  }
  // Ladder rungs on the stern, standing off the wall on brackets, up past the eave.
  {
    const lx = hw - 0.4;
    const lz = hd + 0.22;
    for (const dx of [-0.22, 0.22]) {
      strut('iron', [lx + dx, 0, lz], [lx + dx, EAVE_Y + 0.75, lz], 0.04, 0.04);
      for (const y of [0.9, 2.0]) strut('iron', [lx + dx, y, lz], [lx + dx, y, hd + 0.02], 0.03, 0.03);
    }
    for (let y = 0.3; y < EAVE_Y + 0.5; y += 0.3) strut('iron', [lx - 0.22, y, lz], [lx + 0.22, y, lz], 0.035, 0.035);
  }

  // ---- Navigation lights on the A-pillars ----------------------------------
  for (const sx of [-1, 1]) {
    const f = (2.0 - SILL_TOP) / (TOPY - SILL_TOP);
    const p = lerp2([sx * hw, -hd], topSide(sx, -hd), f);
    const x = p[0] + sx * 0.16;
    const z = p[1];
    withRev((sx > 0 ? 'east' : 'west') === cameraSide, () => {
      strut('iron', [p[0], 2.0, z], [x, 2.0, z], 0.05, 0.05);
      strut('wood', [p[0] + sx * 0.07, 2.02, z - 0.2], [p[0] + sx * 0.07, 2.02, z + 0.2], 0.03, 0.3);
      lampBox(sx < 0 ? 'red' : 'green', x, 2.02, z, 0.12, 0.18, 0.14);
      B('iron', x, 2.14, z, 0.15, 0.04, 0.17);
    });
  }

  // ---- Fixtures (each inside its data collider) ----------------------------
  const byKind = (k: string): { readonly x: number; readonly z: number; readonly w: number; readonly d: number } | null => {
    const i = item(k);
    return i === undefined ? null : { x: i.x, z: i.z, w: i.w, d: i.d };
  };
  const wheel = byKind('wheel');
  if (wheel !== null) {
    const { x, z, w, d } = wheel;
    const aft = z + d / 2;
    const pz = aft - 0.44;
    const hubY = 1.22;
    const wz = aft - 0.14;
    const R = 0.64;
    // Pedestal (cream casing on a plinth) with an access panel to the chain drum.
    B('iron', x, 0.02, pz, 0.62, 0.04, 0.62);
    B('paint', x, 0.1, pz, 0.5, 0.16, 0.5);
    B('paint', x, 0.62, pz, 0.36, 0.96, 0.36);
    B('wood', x, 0.62, pz + 0.185, 0.22, 0.6, 0.02);
    B('paint', x, 1.16, pz + 0.02, 0.3, 0.16, 0.44);
    Cz('iron', x, hubY, wz - 0.16, 0.05, 0.3, 6);
    // Wheel: tan rim and spokes running through to turned handles, tape wrap.
    P('tan', new THREE.TorusGeometry(R, 0.065, 5, 22), x, hubY, wz);
    P('tan', new THREE.TorusGeometry(R * 0.38, 0.035, 4, 12), x, hubY, wz);
    for (let j = 0; j < 8; j += 1) {
      const a = (j * Math.PI) / 4;
      const s = Math.sin(a);
      const c = Math.cos(a);
      strut('tan', [x + s * 0.1, hubY + c * 0.1, wz], [x + s * (R + 0.22), hubY + c * (R + 0.22), wz], 0.075, 0.075, V(0, 0, 1));
      if (j === 0 || j === 3) strut('white', [x + s * (R + 0.06), hubY + c * (R + 0.06), wz], [x + s * (R + 0.19), hubY + c * (R + 0.19), wz], 0.09, 0.09, V(0, 0, 1));
    }
    Cz('iron', x, hubY, wz, 0.13, 0.12, 10);
    Cz('red', x, hubY, wz + 0.07, 0.1, 0.03, 10);
    const wp: readonly P2[] = [
      [-0.06, 0.04],
      [-0.03, -0.04],
      [0, 0.012],
      [0.03, -0.04],
      [0.06, 0.04],
    ];
    for (let j = 0; j < 4; j += 1) {
      const a = at(wp, j);
      const b = at(wp, j + 1);
      strut('white', [x + a[0], hubY + a[1], wz + 0.09], [x + b[0], hubY + b[1], wz + 0.09], 0.016, 0.01, V(0, 0, 1));
    }
    // Rudder cable trunk: flush cover from the pedestal toward the stern.
    B('iron', x, 0.014, (aft + hd - 0.15) / 2, 0.3, 0.012, hd - 0.15 - aft);
    for (let tz = aft + 0.3; tz < hd - 0.3; tz += 0.8) for (const s of [-1, 1]) B('brass', x + s * 0.11, 0.022, tz, 0.03, 0.008, 0.03);
    // Compass binnacle ahead of the wheel, with its lamp and Kelvin spheres.
    const bz = z - d / 2 + 0.3;
    C('wood', x, 0.48, bz, 0.15, 0.92, 8, 0.2);
    C('brass', x, 0.98, bz, 0.21, 0.1, 10);
    P('brass', new THREE.SphereGeometry(0.2, 10, 4, 0, Math.PI * 2, 0, Math.PI / 2), x, 1.02, bz);
    lampBox('amber', x + 0.17, 1.12, bz, 0.07, 0.09, 0.09);
    for (const sx of [-1, 1]) {
      B('brass', x + sx * 0.24, 0.98, bz, 0.1, 0.03, 0.04);
      P('iron', new THREE.SphereGeometry(0.075, 8, 5), x + sx * 0.32, 0.99, bz);
    }
    // Engine telegraph (port of the wheel): brass drum, dial and lever.
    const tx = x - w / 2 + 0.2;
    const tz = z + 0.05;
    C('paint', tx, 0.46, tz, 0.07, 0.92, 6, 0.11);
    B('iron', tx, 0.02, tz, 0.3, 0.04, 0.3);
    Cz('brass', tx, 1.06, tz, 0.18, 0.12, 12);
    Cz('dial', tx, 1.06, tz + 0.065, 0.14, 0.02, 12);
    strut('iron', [tx, 1.06, tz + 0.08], [tx + 0.1, 1.26, tz + 0.09], 0.035, 0.035);
    // Voice pipe cluster to the engine room and cabins: bell mouths, plugs.
    for (const [dx, dz, h] of [
      [0, 0, 1.36],
      [0.12, 0.1, 1.24],
    ] as const) {
      const vx = x + w / 2 - 0.2 + dx;
      const vz = z - 0.1 + dz;
      C('brass', vx, h / 2, vz, 0.04, h, 6);
      P('brass', new THREE.CylinderGeometry(0.1, 0.045, 0.16, 8).rotateX(-Math.PI / 2), vx, h, vz + 0.08);
      B('iron', vx, 0.02, vz, 0.16, 0.03, 0.16);
    }
  }
  const cons = byKind('console');
  if (cons !== null) {
    // 1980s radar/aether-scanner console tucked under the windscreen in the
    // port-forward corner, turned to face the helm (and the street camera).
    const g = new THREE.Group();
    g.position.set(cons.x, 0, cons.z);
    g.rotation.y = Math.atan2(-Math.sign(cons.x) || 1, -Math.sign(cons.z) || 1);
    root.add(g);
    parent = g;
    B('iron', 0, 0.05, -0.02, 1.2, 0.1, 0.62);
    B('paint', 0, 0.44, -0.03, 1.3, 0.68, 0.66);
    B('wood', 0, 0.8, 0, 1.36, 0.05, 0.76);
    for (const dx of [-0.62, 0.62]) B('iron', dx, 0.44, 0.31, 0.02, 0.6, 0.02);
    // Sloped key panel (left) with rows of keys and a red master key.
    const kb = B('paint', -0.3, 0.88, 0.16, 0.62, 0.1, 0.36);
    kb.rotation.x = 0.25;
    for (let r = 0; r < 3; r += 1) for (let k = 0; k < 5; k += 1) B(k === 4 && r === 0 ? 'red' : 'iron', -0.52 + k * 0.11, 0.93 + r * 0.025, 0.26 - r * 0.1, 0.07, 0.03, 0.06);
    // CRT hood (right) with the green radar scope, tilted up toward the pilot.
    const hood = new THREE.Group();
    hood.position.set(0.3, 0.83, -0.1);
    hood.rotation.x = -0.12;
    g.add(hood);
    parent = hood;
    B('paint', 0, 0.32, -0.08, 0.6, 0.64, 0.5);
    B('iron', 0, 0.32, 0.17, 0.54, 0.56, 0.02);
    crt('#061a0e', 0, 0.32, 0.185, 0.46, 0.44, 0.01);
    for (const r of [0.19, 0.13, 0.07]) crtG('#2f8a4c', new THREE.TorusGeometry(r, 0.005, 3, 20), 0, 0.32, 0.192);
    crt('#2f8a4c', 0, 0.32, 0.192, 0.008, 0.4, 0.004);
    crt('#2f8a4c', 0, 0.32, 0.192, 0.42, 0.008, 0.004);
    const sweep = crt('#9ef0a8', 0.07, 0.38, 0.195, 0.18, 0.014, 0.004);
    sweep.rotation.z = 0.8;
    crt('#b8ffc0', -0.09, 0.41, 0.196, 0.03, 0.03, 0.004);
    parent = g;
    crt('#79d892', 0.3, 0.88, 0.29, 0.16, 0.05, 0.01);
    // Cable from the hood's back down into the wainscot gland (aerial lead-in).
    strut('iron', [0.3, 1.2, -0.37], [0.3, 0.9, -0.46], 0.04, 0.04);
    strut('iron', [0.3, 0.9, -0.46], [0.3, 0.5, -0.46], 0.04, 0.04);
    parent = root;
  }
  const seat = byKind('seat');
  if (seat !== null) {
    const { x, z } = seat;
    const sz = z + 0.02;
    // Pilot seat right behind the wheel: bolted flange, pedestal post, swivel,
    // blue padded shell with headrest and armrests.
    C('iron', x, 0.02, sz, 0.3, 0.04, 12);
    C('iron', x, 0.27, sz, 0.07, 0.5, 8);
    C('paint', x, 0.53, sz, 0.17, 0.06, 10);
    B('blue', x, 0.64, sz - 0.02, 0.62, 0.16, 0.6);
    B('blue', x, 0.74, sz - 0.31, 0.58, 0.06, 0.06);
    const bk = B('blue', x, 1.08, sz + 0.3, 0.6, 0.8, 0.14);
    bk.rotation.x = 0.14;
    B('blue', x, 1.58, sz + 0.38, 0.38, 0.22, 0.13);
    for (const s of [-1, 1]) {
      B('paint', x + s * 0.35, 0.9, sz - 0.04, 0.08, 0.07, 0.46);
      B('iron', x + s * 0.35, 0.78, sz + 0.13, 0.05, 0.2, 0.05);
      B('blue', x + s * 0.32, 0.78, sz + 0.05, 0.04, 0.22, 0.5);
    }
    P('iron', new THREE.TorusGeometry(0.22, 0.02, 3, 12).rotateX(Math.PI / 2), x, 0.22, sz);
    // Throttle quadrant on the port arm: two engine levers.
    const ox = x - 0.45;
    B('iron', ox, 0.86, sz - 0.1, 0.12, 0.12, 0.24);
    for (const dz of [-0.05, 0.05]) strut('red', [ox, 0.9, sz - 0.1 + dz], [ox, 1.06, sz - 0.16 + dz], 0.028, 0.028);
  }
  // Ship's clock and aneroid barometer on the port bulkhead, over the flag locker.
  {
    const lk = item('flag-locker');
    const zc = lk === undefined ? 2.3 : lk.z;
    for (const [v, k] of [
      [-0.35, 'brass'],
      [0.35, 'wood'],
    ] as const) {
      const y = 1.75;
      const lean = (LEAN * (y - SILL_TOP)) / (TOPY - SILL_TOP);
      const px = -hw + lean + 0.1;
      Cx(k, px, y, zc + v, 0.16, 0.06, 14);
      Cx('dial', px + 0.035, y, zc + v, 0.13, 0.02, 14);
      strut('iron', [px + 0.05, y, zc + v], [px + 0.05, y + 0.1, zc + v + 0.02], 0.014, 0.008);
    }
  }
  // Overhead carling along the centreline ties the headers together and
  // carries the bridge lamp.
  {
    const ly = TOPY + 0.12;
    const lz = 1.3;
    strut('beam', [0, ly, TNOSE.z + 0.05], [0, ly, hd - LEAN - 0.1], 0.18, 0.22);
    strut('iron', [0, ly - 0.11, lz], [0, 2.1, lz], 0.014, 0.014);
    C('iron', 0, 2.08, lz, 0.07, 0.07, 8, 0.14);
    lampBall('amber', 0, 1.99, lz, 0.085);
    strut('iron', [-0.08, 1.99, lz], [0.08, 1.99, lz], 0.01, 0.01);
  }
  const chart = byKind('chart-table');
  if (chart !== null) {
    const { x, z, w, d } = chart;
    // The user side faces into the room.
    const fz = z < 0 ? 1 : -1;
    const H = 0.95;
    // Chart table: drawer cabinet (flat charts), lipped top, chart, dividers.
    B('iron', x, 0.05, z, w - 0.1, 0.1, d - 0.1);
    B('wood', x, 0.5, z + fz * 0.05, w - 0.06, 0.8, d - 0.15);
    for (let r = 0; r < 3; r += 1) {
      B('paint', x - w * 0.2, 0.3 + r * 0.2, z + fz * (d / 2 - 0.09), w * 0.5, 0.02, 0.01);
      B('brass', x - w * 0.2, 0.22 + r * 0.2 + 0.1, z + fz * (d / 2 - 0.08), 0.1, 0.025, 0.02);
    }
    const top = B('wood', x, H - 0.03, z, w, 0.06, d);
    top.rotation.x = fz * -0.04;
    for (const s of [-1, 1]) B('beam', x, H + 0.01, z + s * (d / 2 - 0.02), w, 0.03, 0.03);
    B('chart', x + 0.05, H + 0.005, z, w * 0.75, 0.006, d * 0.7);
    B('grout', x - 0.1, H + 0.009, z + 0.05, w * 0.3, 0.002, 0.01);
    B('grout', x + 0.25, H + 0.009, z - 0.12, 0.01, 0.002, d * 0.3);
    // Dividers (brass, legs apart) and a parallel rule.
    strut('brass', [x + 0.15, H + 0.02, z + 0.1], [x + 0.3, H + 0.09, z], 0.015, 0.015);
    strut('brass', [x + 0.45, H + 0.02, z + 0.08], [x + 0.3, H + 0.09, z], 0.015, 0.015);
    B('wood', x - 0.35, H + 0.02, z - 0.15, 0.5, 0.02, 0.06);
    // Stool tucked under the user side.
    const sx = x + w * 0.25;
    const sz = z + fz * (d / 2 - 0.25);
    C('iron', sx, 0.3, sz, 0.04, 0.6, 6);
    C('iron', sx, 0.02, sz, 0.16, 0.03, 8);
    C('tan', sx, 0.62, sz, 0.17, 0.05, 10);
  }
  const locker = byKind('flag-locker');
  if (locker !== null) {
    const { x, z, w, d } = locker;
    // Opens toward the room.
    const fx = x > 0 ? -1 : 1;
    const H = 0.5;
    // Signal-flag locker: pigeonholes with rolled flags, bench lid on top.
    B('wood', x, H / 2, z, w, H, d);
    B('tan', x, H + 0.02, z, w + 0.04, 0.04, d + 0.04);
    const cols = 5;
    const rows = 2;
    const colsFlag = ['red', 'yellow', 'blue', 'white', 'red', 'blue', 'yellow', 'white', 'yellow', 'red'] as const;
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const pz = z - d / 2 + ((c + 0.5) * d) / cols;
        const py = 0.12 + r * 0.19;
        B('beam', x + fx * (w / 2 - 0.02), py + 0.08, pz, 0.02, 0.16, d / cols - 0.05);
        Cx(at(colsFlag, r * cols + c), x + fx * (w / 2 - 0.01), py + 0.08, pz, 0.065, 0.03, 8);
      }
    }
  }
  // Level 2: a navigator's jump seat folds down from the stern bulkhead.
  if (room.level > 1) {
    const jx = hw - 1.4;
    B('blue', jx, 0.55, hd - 0.19, 0.5, 0.08, 0.12);
    B('blue', jx, 0.95, hd - 0.15, 0.5, 0.5, 0.06);
    for (const s of [-1, 1]) B('iron', jx + s * 0.27, 0.55, hd - 0.17, 0.03, 0.2, 0.1);
  }
  return root;
};
